import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, Loader2, GraduationCap, Users, Award, TrendingUp, CalendarDays, Mail } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Separator } from '@/shared/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useConfiguracionCEMC, useEstadisticasCEMC } from '../hooks/useCEMC'
import { cemcApi } from '../api/cemc.api'
import { MetricaCard } from '../components/MetricaCard'
import { ModuloProgressTable } from '../components/ModuloProgressTable'
import { AprobacionChart } from '../components/AprobacionChart'
import { InscripcionesChart } from '../components/InscripcionesChart'
import { UsuariosModuloTable } from '../components/UsuariosModuloTable'
import { CursosTotalesChart } from '../components/CursosTotalesChart'
import { ConversionChart } from '../components/ConversionChart'

export function ReporteCEMCPage() {
  const { configId } = useParams<{ configId: string }>()
  const navigate = useNavigate()
  const id = Number(configId)
  const [isExporting, setIsExporting] = useState(false)
  const [dimension, setDimension] = useState<'representante' | 'ciudad'>('representante')

  const { data: config, isLoading: configLoading } = useConfiguracionCEMC(id)
  const { data: stats, isLoading: statsLoading } = useEstadisticasCEMC(id)

  const totalTasaGraduacion = stats?.cursos?.length
    ? Math.round(stats.cursos.reduce((sum, c) => sum + c.tasa_graduacion, 0) / stats.cursos.length)
    : 0

  const handleExportar = async () => {
    setIsExporting(true)
    try {
      const res = await cemcApi.exportar(id)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `cemc_${id}_export.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
    } finally {
      setIsExporting(false)
    }
  }

  if (configLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/cemc-educacion')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-violet-600" />
              {config?.titulo_reporte}
            </h1>
            <p className="text-sm text-muted-foreground">{config?.grupo_nombre}</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleExportar} disabled={isExporting}>
          {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          Exportar Excel
        </Button>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricaCard
          title="Total Inscritos"
          value={statsLoading ? '—' : (stats?.total_inscritos ?? 0).toLocaleString()}
          icon={Users}
          color="violet"
          isLoading={statsLoading}
          footer={
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Participantes</span>
                <span className="font-semibold text-green-600">
                  {(stats?.total_iniciaron ?? 0).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">No Participantes</span>
                <span className="font-semibold text-rose-600">
                  {(stats?.total_no_iniciaron ?? 0).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">% Participación</span>
                <span className="font-semibold text-blue-600">
                  {(stats?.porcentaje_participacion ?? 0).toLocaleString()}%
                </span>
              </div>
            </div>
          }
        />
        <MetricaCard
          title="Total Graduados"
          value={statsLoading ? '—' : (stats?.total_graduados ?? 0).toLocaleString()}
          icon={Award}
          color="green"
          isLoading={statsLoading}
        />
        <MetricaCard
          title="Tasa Graduación"
          value={statsLoading ? '—' : `${totalTasaGraduacion}%`}
          icon={TrendingUp}
          color="blue"
          isLoading={statsLoading}
        />
        <MetricaCard
          title="Inscritos Hoy"
          value={statsLoading ? '—' : (stats?.inscritos_hoy ?? 0).toLocaleString()}
          icon={CalendarDays}
          color="purple"
          isLoading={statsLoading}
        />
      </div>

      {/* Gráfica de inscripciones */}
      {stats?.inscritos_por_dia?.length ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Inscritos por día (últimos 30 días)</CardTitle>
          </CardHeader>
          <CardContent>
            <InscripcionesChart data={stats.inscritos_por_dia} />
          </CardContent>
        </Card>
      ) : null}

      {/* Inscritos por curso */}
      {stats?.cursos?.length ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Progreso de Médicos</CardTitle>
          </CardHeader>
          <CardContent>
            <CursosTotalesChart cursos={stats.cursos} />
          </CardContent>
        </Card>
      ) : null}

      {/* Registros vs Participantes por representante / ciudad */}
      {stats?.por_representante?.length || stats?.por_ciudad?.length ? (
        <Card>
          <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-base">Registros vs Participantes</CardTitle>
              <CardDescription className="text-xs">
                Conversión por {dimension === 'representante' ? 'representante' : 'ciudad'} · detalle en el tooltip
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden items-center gap-3 text-xs text-muted-foreground sm:flex">
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-violet-600" />Registros</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-600" />Participantes</span>
              </div>
              <div className="inline-flex rounded-lg border p-0.5">
                <Button
                  variant={dimension === 'representante' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7"
                  onClick={() => setDimension('representante')}
                >
                  Representante
                </Button>
                <Button
                  variant={dimension === 'ciudad' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7"
                  onClick={() => setDimension('ciudad')}
                >
                  Ciudad
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[440px] overflow-y-auto pr-2">
              <ConversionChart data={(dimension === 'representante' ? stats?.por_representante : stats?.por_ciudad) ?? []} />
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Errores de diagnóstico */}
      {stats?.errores?.length ? (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-800 dark:text-amber-400">
              Advertencias de configuración
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {stats.errores.map((err, i) => (
              <p key={i} className="text-xs text-amber-700 dark:text-amber-300 font-mono">{err}</p>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {/* Tabs por curso */}
      {statsLoading ? (
        <Skeleton className="h-48" />
      ) : stats?.cursos?.length ? (
        <Tabs defaultValue={stats.cursos[0]?.codigo}>
          <TabsList className="flex-wrap h-auto w-full">
            {stats.cursos.map((c) => (
              <TabsTrigger key={c.codigo} value={c.codigo} className="flex-1">{c.nombre}</TabsTrigger>
            ))}
          </TabsList>
          {stats.cursos.map((curso) => (
            <TabsContent key={curso.codigo} value={curso.codigo} className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Inscritos</CardDescription></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{curso.total_inscritos.toLocaleString()}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Graduados</CardDescription></CardHeader>
                  <CardContent><div className="text-2xl font-bold text-green-700">{curso.total_graduados.toLocaleString()}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Tasa Graduación</CardDescription></CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-violet-700">{curso.tasa_graduacion}%</div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid md:grid-cols-2 gap-4 items-stretch">
                <Card>
                  <CardHeader><CardTitle className="text-sm">Progreso por módulo</CardTitle></CardHeader>
                  <CardContent>
                    <ModuloProgressTable modulos={curso.modulos} />
                  </CardContent>
                </Card>
                <Card className="flex flex-col">
                  <CardHeader><CardTitle className="text-sm">Aprobados por módulo</CardTitle></CardHeader>
                  <CardContent className="flex-1 flex flex-col pb-4">
                    <AprobacionChart modulos={curso.modulos} />
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Detalle por usuario</CardTitle>
                  <CardDescription className="text-xs">
                    <span className="inline-flex items-center gap-3">
                      <span className="flex items-center gap-1 text-green-600">✓ Aprobado</span>
                      <span className="flex items-center gap-1 text-amber-500">⚠ Intentó / sin puntaje mínimo</span>
                      <span className="flex items-center gap-1 text-red-500">✗ No intentó</span>
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <UsuariosModuloTable
                    configId={id}
                    curso={{ ...curso, id: curso.curso_id }}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <GraduationCap className="mx-auto h-12 w-12 mb-4 opacity-30" />
            <p>Sin datos de cursos para mostrar</p>
          </CardContent>
        </Card>
      )}

      {/* Footer emails */}
      {stats && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-6 text-sm">
              <span className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-green-600" />
                <span className="text-muted-foreground">Emails validados:</span>
                <span className="font-semibold">{stats.email_validados.toLocaleString()}</span>
              </span>
              <Separator orientation="vertical" className="h-4" />
              <span className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-yellow-600" />
                <span className="text-muted-foreground">Emails pendientes:</span>
                <span className="font-semibold">{stats.email_pendientes.toLocaleString()}</span>
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
